import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  FaUser,
  FaHashtag,
  FaSmile,
  FaLink,
  FaSpinner,
  FaUserCheck,
} from "react-icons/fa";

/**
 * MessageStep
 * ------------------------------------------------------------------
 * Step 4 of the brand DM automation wizard – the final step before
 * submission.
 *
 * Brands compose the DM body that will be sent to commenters whose
 * comment matched one of the configured keywords. We expose a small
 * set of placeholders that are resolved server-side at send time:
 *
 *   {{name}}          → comment author display name
 *   {{username}}      → @handle
 *   {{first_name}}    → first token of the username
 *   {{product_link}}  → value provided in the Keywords step
 *
 * Brands can also toggle "Send a public reply to the comment first"
 * (post a reply under the original comment) – mirror of the
 * influencer flow.
 */
const PLACEHOLDERS = [
  { key: "{{name}}", label: "Name", Icon: FaUser },
  { key: "{{username}}", label: "Username", Icon: FaHashtag },
  { key: "{{first_name}}", label: "First Name", Icon: FaSmile },
  { key: "{{product_link}}", label: "Product Link", Icon: FaLink },
];

const PLACEHOLDER_SPLIT_RE =
  /({{product_link}}|{{name}}|{{username}}|{{first_name}})/g;

// Mirror of the server-side defaults (dmTemplateBuilder.DEFAULTS) so the
// UI can preview exactly what an empty field will send.
const GATE_DEFAULTS = {
  accessPromptMessage:
    "Hey there! Glad you're here 😊\nTap below and I'll send you the access in just a moment ✨",
  accessButtonTitle: "Send me the access",
  followPromptMessage:
    "Almost there!\nPlease visit my profile and tap follow to continue 😄",
  visitProfileButtonTitle: "Visit Profile",
  followingButtonTitle: "I'm following ✅",
};

const EMPTY_GATE = {
  enabled: false,
  accessPromptMessage: "",
  accessButtonTitle: "",
  followPromptMessage: "",
  visitProfileButtonTitle: "",
  followingButtonTitle: "",
  profileUrl: "",
};

/** Pre-fill empty gate copy fields with server-side defaults when the gate is on. */
function applyGateDefaults(gate) {
  if (!gate.enabled) return gate;
  return {
    ...gate,
    accessPromptMessage:
      gate.accessPromptMessage || GATE_DEFAULTS.accessPromptMessage,
    accessButtonTitle:
      gate.accessButtonTitle || GATE_DEFAULTS.accessButtonTitle,
    followPromptMessage:
      gate.followPromptMessage || GATE_DEFAULTS.followPromptMessage,
    visitProfileButtonTitle:
      gate.visitProfileButtonTitle || GATE_DEFAULTS.visitProfileButtonTitle,
    followingButtonTitle:
      gate.followingButtonTitle || GATE_DEFAULTS.followingButtonTitle,
  };
}

function renderPreview(message, productLink) {
  if (!message) return null;
  return message.split(PLACEHOLDER_SPLIT_RE).map((part, index) => {
    if (part === "{{product_link}}") {
      return productLink ? (
        <a
          key={index}
          href={productLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
        >
          {productLink}
        </a>
      ) : (
        <span key={index} className="italic text-gray-400">
          [product link]
        </span>
      );
    }
    if (part === "{{name}}") return <span key={index}>Alex Johnson</span>;
    if (part === "{{username}}") return <span key={index}>@alex_brand</span>;
    if (part === "{{first_name}}") return <span key={index}>Alex</span>;
    return <span key={index}>{part}</span>;
  });
}

const MessageStep = ({
  data,
  onBack,
  onSubmit,
  isSubmitting,
  isEditMode = false,
}) => {
  const [message, setMessage] = useState(data?.message || "");
  const [productLink, setProductLink] = useState(data?.productLink || "");
  const [sendReplyToCommentFirst, setSendReplyToCommentFirst] = useState(
    !!data?.sendReplyToCommentFirst
  );
  const [replyCommentMessage, setReplyCommentMessage] = useState(
    data?.replyCommentMessage || "Thanks for the comment! Check your DM 🙌"
  );
  const [gate, setGate] = useState(() =>
    applyGateDefaults({
      ...EMPTY_GATE,
      ...(data?.followerGate || {}),
    })
  );

  useEffect(() => {
    if (data?.message !== undefined) setMessage(data.message || "");
    if (data?.productLink !== undefined) setProductLink(data.productLink || "");
    if (data?.sendReplyToCommentFirst !== undefined) {
      setSendReplyToCommentFirst(!!data.sendReplyToCommentFirst);
    }
    if (data?.replyCommentMessage !== undefined && data.replyCommentMessage) {
      setReplyCommentMessage(data.replyCommentMessage);
    }
    if (data?.followerGate !== undefined) {
      setGate(
        applyGateDefaults({ ...EMPTY_GATE, ...(data.followerGate || {}) })
      );
    }
  }, [
    data?.message,
    data?.productLink,
    data?.sendReplyToCommentFirst,
    data?.replyCommentMessage,
    data?.followerGate,
  ]);

  const handleInsert = (token) => {
    setMessage((prev) => (prev?.trim() ? `${prev} ${token}` : token));
  };

  const updateGate = (patch) => setGate((prev) => ({ ...prev, ...patch }));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!message.trim()) {
      toast.error("Please write a DM message");
      return;
    }
    if (sendReplyToCommentFirst && !replyCommentMessage.trim()) {
      toast.error(
        "Either turn off comment reply, or provide a comment reply message"
      );
      return;
    }

    const normalizedGate = applyGateDefaults(gate);

    onSubmit({
      message: message.trim(),
      sendReplyToCommentFirst,
      replyCommentMessage: sendReplyToCommentFirst
        ? replyCommentMessage.trim()
        : "",
      followerGate: {
        enabled: normalizedGate.enabled,
        accessPromptMessage: normalizedGate.accessPromptMessage.trim(),
        accessButtonTitle: normalizedGate.accessButtonTitle.trim(),
        followPromptMessage: normalizedGate.followPromptMessage.trim(),
        visitProfileButtonTitle: normalizedGate.visitProfileButtonTitle.trim(),
        followingButtonTitle: normalizedGate.followingButtonTitle.trim(),
        profileUrl: normalizedGate.profileUrl.trim(),
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <header>
        <h2 className="text-base md:text-xl font-semibold text-gray-900">
          Direct Message
        </h2>
        <p className="mt-1 text-[11px] md:text-sm text-gray-600">
          Compose the personalised DM each matched commenter will receive.
        </p>
      </header>

      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
          Insert a placeholder
        </p>
        <div className="flex flex-wrap gap-2">
          {PLACEHOLDERS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleInsert(key)}
              className="flex items-center gap-1.5 rounded-md bg-orange-50 px-2.5 py-1.5 text-xs font-medium text-orange-700 transition hover:bg-orange-100"
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs md:text-sm font-medium text-gray-700">
          Message Body
        </label>
        <textarea
          rows={6}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Hey {{first_name}}! Thanks for your comment — here's the link: {{product_link}}"
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-3 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        />
      </div>

      {message && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Preview
          </p>
          <p className="whitespace-pre-wrap text-sm text-gray-700">
            {renderPreview(message, productLink)}
          </p>
        </div>
      )}

      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Reply to the comment after sending the DM
            </p>
            <p className="mt-1 text-[11px] md:text-xs text-gray-500">
              Posts a short public reply on the original comment, only after
              the DM is delivered successfully.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSendReplyToCommentFirst((prev) => !prev)}
            aria-pressed={sendReplyToCommentFirst}
            className={`relative inline-flex h-6 w-12 shrink-0 items-center rounded-full transition-colors ${
              sendReplyToCommentFirst
                ? "bg-orange-500 hover:bg-orange-600"
                : "bg-gray-300 hover:bg-gray-400"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                sendReplyToCommentFirst ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {sendReplyToCommentFirst && (
          <div className="mt-3">
            <label className="block text-xs md:text-sm font-medium text-gray-700">
              Comment Reply
            </label>
            <input
              type="text"
              value={replyCommentMessage}
              onChange={(event) => setReplyCommentMessage(event.target.value)}
              placeholder="Thanks! DM'd you the link 🙌"
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            />
          </div>
        )}
      </div>

      {/* Follower gate ("follow to unlock") */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <FaUserCheck className="h-4 w-4 text-orange-500" />
              Require a follow before sending the link
            </p>
            <p className="mt-1 text-[11px] md:text-xs text-gray-500">
              Instead of sending the link right away, the user is asked to
              follow you first. We verify the follow, then unlock the link
              automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (gate.enabled) {
                updateGate({ enabled: false });
              } else {
                updateGate(applyGateDefaults({ ...gate, enabled: true }));
              }
            }}
            aria-pressed={gate.enabled}
            className={`relative inline-flex h-6 w-12 shrink-0 items-center rounded-full transition-colors ${
              gate.enabled
                ? "bg-orange-500 hover:bg-orange-600"
                : "bg-gray-300 hover:bg-gray-400"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                gate.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {gate.enabled && (
          <div className="mt-4 space-y-4 rounded-xl border border-orange-100 bg-orange-50/40 p-4">
            <div className="rounded-lg bg-white/70 p-3 text-[11px] text-gray-600">
              Flow: <span className="font-medium">comment</span> →{" "}
              <span className="font-medium">access prompt</span> →{" "}
              <span className="font-medium">follow gate</span> → verify follow →{" "}
              <span className="font-medium">unlock the message above</span>.
              Default copy is pre-filled — edit any field as needed.
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700">
                Step 1 — Access prompt message
              </label>
              <textarea
                rows={2}
                value={gate.accessPromptMessage}
                onChange={(e) =>
                  updateGate({ accessPromptMessage: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
              <input
                type="text"
                value={gate.accessButtonTitle}
                maxLength={20}
                onChange={(e) =>
                  updateGate({ accessButtonTitle: e.target.value })
                }
                placeholder="Button label"
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700">
                Step 2 — Follow gate message
              </label>
              <textarea
                rows={2}
                value={gate.followPromptMessage}
                onChange={(e) =>
                  updateGate({ followPromptMessage: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  type="text"
                  value={gate.visitProfileButtonTitle}
                  maxLength={20}
                  onChange={(e) =>
                    updateGate({ visitProfileButtonTitle: e.target.value })
                  }
                  placeholder="Visit profile button"
                  className="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                />
                <input
                  type="text"
                  value={gate.followingButtonTitle}
                  maxLength={20}
                  onChange={(e) =>
                    updateGate({ followingButtonTitle: e.target.value })
                  }
                  placeholder="Following confirmation button"
                  className="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700">
                Profile URL{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                type="url"
                value={gate.profileUrl}
                onChange={(e) => updateGate({ profileUrl: e.target.value })}
                placeholder="https://www.instagram.com/yourbrand (auto-detected if blank)"
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
              <p className="mt-1 text-[11px] text-gray-500">
                Used for the “Visit Profile” button. Defaults to your connected
                Instagram account.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200 disabled:opacity-60"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !message.trim()}
          className="flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-orange-300"
        >
          {isSubmitting && <FaSpinner className="h-3.5 w-3.5 animate-spin" />}
          {isSubmitting
            ? isEditMode
              ? "Updating…"
              : "Creating…"
            : isEditMode
            ? "Update Automation"
            : "Create Automation"}
        </button>
      </div>
    </form>
  );
};

export default MessageStep;
